import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { adminAxios, formatApiError } from "../../lib/adminApi";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export default function AdminStats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await adminAxios.get("/stats");
        setStats(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await adminAxios.put("/admin/stats", {
        years_experience: parseInt(stats.years_experience) || 0,
        happy_clients: parseInt(stats.happy_clients) || 0,
        projects_completed: parseInt(stats.projects_completed) || 0,
        rating: parseFloat(stats.rating) || 0,
        review_count: parseInt(stats.review_count) || 0,
      });
      toast.success("Stats updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !stats) {
    return <div className="py-20 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#1C6B8A]" /></div>;
  }

  const field = (key, label, hint) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input
        type="number"
        step={key === "rating" ? "0.1" : "1"}
        value={stats[key] ?? ""}
        onChange={(e) => setStats({ ...stats, [key]: e.target.value })}
        data-testid={`stats-${key}`}
      />
      {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
    </div>
  );

  return (
    <div data-testid="admin-stats-page" className="space-y-6">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Trust stats & rating</h1>
        <p className="text-slate-500 text-sm mt-1">Numbers shown on Home, About and in SEO schema.</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6 card-soft space-y-4 max-w-2xl">
        <div className="grid md:grid-cols-3 gap-4">
          {field("years_experience", "Years of Experience", "e.g. 15")}
          {field("happy_clients", "Happy Clients", "e.g. 10000")}
          {field("projects_completed", "Projects Completed", "e.g. 12000")}
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {field("rating", "Google Rating", "e.g. 4.9 (1 decimal)")}
          {field("review_count", "Google Review Count", "e.g. 310")}
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="stats-save-btn"
          className="h-11 px-6 rounded-full brand-btn text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save changes
        </button>
      </div>
    </div>
  );
}
